import React from "react";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Clock, Beaker, Droplets, Compass } from "lucide-react";
import { getTotalDepth } from "@/lib/depthCalculations";

function gainLossColor(val) {
  if (val == null) return "text-slate-400 bg-slate-100 dark:text-slate-500 dark:bg-slate-800";
  if (val >= 0) return "text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950"; // gain
  return "text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-950"; // loss
}

export default function DrillRunRow({ run, onEdit, onDelete, displayNumber, holeType, rodLength }) {
  const fromDepth = Number(run.from_depth) || 0;
  const displayToDepth = getTotalDepth(run, holeType, rodLength);
  const depth = Number.isFinite(displayToDepth) && Number.isFinite(fromDepth)
    ? Math.max(0, displayToDepth - fromDepth).toFixed(2)
    : "0.00";

  return (
    <div className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 hover:border-slate-300 transition-colors dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm dark:text-slate-100">
            Run #{displayNumber ?? run.run_number}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {run.from_depth}–{displayToDepth} m <span className="text-slate-400">({depth} m)</span>
          </span>
          {run.start_time && run.end_time && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Clock className="w-3 h-3" />{run.start_time}–{run.end_time}
            </span>
          )}
          {run.gain_loss != null && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${gainLossColor(run.gain_loss)}`}>
              G/L {run.gain_loss > 0 ? "+" : ""}{run.gain_loss} m
            </span>
          )}
          {holeType !== "Conventional" && run.viscosity_check != null && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-400">
              <Beaker className="w-3 h-3" /> {run.viscosity_check}s
            </span>
          )}
          {run.flow_rate != null && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-400">
              <Droplets className="w-3 h-3" /> {run.flow_rate} L/min
            </span>
          )}
          {run.azimuth != null && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-slate-100 dark:bg-slate-800"><Compass className="w-3 h-3"/> {run.azimuth}°</span>}
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center gap-0.5 shrink-0">
            {onEdit && (
              <Button variant="ghost" size="icon" onClick={onEdit} className="h-11 w-11 md:h-9 md:w-9">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="icon" onClick={onDelete} className="h-11 w-11 md:h-9 md:w-9 text-red-500 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
      {(run.driller || run.lithology || run.remarks) && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600 dark:text-slate-300">
          {run.driller && (
            <span><span className="text-slate-400 dark:text-slate-500">Driller:</span> {run.driller}</span>
          )}
          {run.lithology && (
            <span><span className="text-slate-400 dark:text-slate-500">Lith:</span> {run.lithology}</span>
          )}
        </div>
      )}
      {run.remarks && (
        <p className="text-xs text-slate-500 mt-1 italic line-clamp-1 dark:text-slate-400">
          {run.remarks}
        </p>
      )}
      {run.date && (
        <p className="text-[11px] text-slate-400 mt-0.5 dark:text-slate-500">{run.date}</p>
      )}
    </div>
  );
}